import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Settings, 
  Mail, 
  Server,
  Lock,
  Send,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff,
  Building2,
  Loader2,
  Info
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminSettingsPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeAccount, setActiveAccount] = useState('preventivi');
  
  // Stato SMTP Preventivi
  const [smtpPreventivi, setSmtpPreventivi] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_encryption: 'SSL',
    smtp_from_name: '',
    smtp_verified: false
  });
  
  // Stato SMTP Supporto
  const [smtpSupporto, setSmtpSupporto] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_encryption: 'SSL',
    smtp_from_name: '',
    smtp_verified: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  // Fetch configurazione SMTP attuale
  const fetchSmtpConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/smtp-config`, {
        headers: getAuthHeader()
      });
      
      if (response.data.success && response.data.config) {
        const config = response.data.config;
        
        // Carica config Preventivi
        if (config.preventivi) {
          setSmtpPreventivi(prev => ({
            ...prev,
            smtp_host: config.preventivi.smtp_host || '',
            smtp_port: config.preventivi.smtp_port || 465,
            smtp_user: config.preventivi.smtp_user || '',
            smtp_password: '',  // Non viene mai restituita
            smtp_encryption: config.preventivi.smtp_encryption || 'SSL',
            smtp_from_name: config.preventivi.smtp_from_name || '',
            smtp_verified: config.preventivi.smtp_verified || false
          }));
        }
        
        // Carica config Supporto
        if (config.supporto) {
          setSmtpSupporto(prev => ({
            ...prev,
            smtp_host: config.supporto.smtp_host || '',
            smtp_port: config.supporto.smtp_port || 465,
            smtp_user: config.supporto.smtp_user || '',
            smtp_password: '',
            smtp_encryption: config.supporto.smtp_encryption || 'SSL',
            smtp_from_name: config.supporto.smtp_from_name || '',
            smtp_verified: config.supporto.smtp_verified || false
          }));
        }
      }
    } catch (error) {
      toast.error('Errore nel caricamento della configurazione SMTP');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchSmtpConfig();
  }, [fetchSmtpConfig]);

  // Gestori per i due account
  const getCurrentSmtpData = () => activeAccount === 'preventivi' ? smtpPreventivi : smtpSupporto;
  const setCurrentSmtpData = (data) => {
    if (activeAccount === 'preventivi') {
      setSmtpPreventivi(data);
    } else {
      setSmtpSupporto(data);
    }
  };

  const handleSmtpChange = (field, value) => {
    const currentData = getCurrentSmtpData();
    setCurrentSmtpData({
      ...currentData,
      [field]: value
    });
  };

  // Test SMTP
  const handleTestSmtp = async () => {
    const currentData = getCurrentSmtpData();
    
    if (!currentData.smtp_host || !currentData.smtp_user || !currentData.smtp_password) {
      toast.error('Compila tutti i campi obbligatori (Host, User, Password)');
      return;
    }
    
    setTestingSmtp(true);
    try {
      const response = await axios.post(`${API}/admin/smtp-config/test`, {
        account_type: activeAccount,
        smtp_host: currentData.smtp_host,
        smtp_port: currentData.smtp_port,
        smtp_user: currentData.smtp_user,
        smtp_password: currentData.smtp_password,
        smtp_encryption: currentData.smtp_encryption,
        smtp_from_name: currentData.smtp_from_name
      }, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setCurrentSmtpData({ ...currentData, smtp_verified: true });
      } else {
        toast.error(response.data.message);
        setCurrentSmtpData({ ...currentData, smtp_verified: false });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il test SMTP');
      console.error(error);
    } finally {
      setTestingSmtp(false);
    }
  };

  // Salva SMTP (senza test)
  const handleSaveSmtp = async () => {
    const currentData = getCurrentSmtpData();
    
    if (!currentData.smtp_host || !currentData.smtp_user || !currentData.smtp_password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    setSavingSmtp(true);
    try {
      await axios.put(`${API}/admin/smtp-config`, {
        account_type: activeAccount,
        smtp_host: currentData.smtp_host,
        smtp_port: currentData.smtp_port,
        smtp_user: currentData.smtp_user,
        smtp_password: currentData.smtp_password,
        smtp_encryption: currentData.smtp_encryption,
        smtp_from_name: currentData.smtp_from_name
      }, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      
      toast.success('Configurazione salvata. Esegui il test per verificare.');
      setCurrentSmtpData({ ...currentData, smtp_verified: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
      console.error(error);
    } finally {
      setSavingSmtp(false);
    }
  };

  // Verifica se l'utente è Super Admin (email specifica o ruolo super_admin)
  const isSuperAdmin = user?.email === 'l.manenti@machenoleggio.it' || user?.role === 'super_admin';
  
  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">⛔ Accesso riservato al Super Admin</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentData = getCurrentSmtpData();

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Settings className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Impostazioni Sede Centrale
          </h1>
          <p className="text-slate-500">Configurazione SMTP e parametri di sistema</p>
        </div>
      </div>

      {/* Info Box */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Gestione autonoma delle credenziali SMTP</p>
              <p className="mt-1 text-blue-700">
                Configura qui le credenziali email della Sede Centrale. Se le credenziali cambiano, 
                puoi aggiornarle e testarle immediatamente senza interventi tecnici.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configurazione SMTP */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Configurazione Email SMTP</CardTitle>
                <CardDescription>
                  Configura i server di posta per l'invio delle email
                </CardDescription>
              </div>
            </div>
            {currentData.smtp_verified ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                Verificato
              </Badge>
            ) : currentData.smtp_host && (
              <Badge variant="outline" className="border-amber-300 text-amber-700">
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Non verificato
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Selezione Account */}
          <Tabs value={activeAccount} onValueChange={setActiveAccount}>
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="preventivi" className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                Preventivi
                {smtpPreventivi.smtp_verified && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                )}
              </TabsTrigger>
              <TabsTrigger value="supporto" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Supporto
                {smtpSupporto.smtp_verified && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value={activeAccount} className="mt-6">
              <div className="p-4 bg-slate-50 rounded-lg mb-4">
                <p className="text-sm text-slate-600">
                  <strong>Account {activeAccount === 'preventivi' ? 'Preventivi' : 'Supporto'}:</strong>{' '}
                  {activeAccount === 'preventivi' 
                    ? 'Utilizzato per l\'invio delle offerte e comunicazioni commerciali'
                    : 'Utilizzato per reset password e notifiche di sistema'}
                </p>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                {/* Host SMTP */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-slate-500" />
                    Host SMTP *
                  </Label>
                  <Input
                    placeholder="es. smtps.aruba.it"
                    value={currentData.smtp_host}
                    onChange={(e) => handleSmtpChange('smtp_host', e.target.value)}
                  />
                </div>
                
                {/* Porta */}
                <div className="space-y-2">
                  <Label>Porta *</Label>
                  <Input
                    type="number"
                    placeholder="465"
                    value={currentData.smtp_port}
                    onChange={(e) => handleSmtpChange('smtp_port', parseInt(e.target.value) || 465)}
                  />
                </div>
                
                {/* User */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    Utente (Email) *
                  </Label>
                  <Input
                    type="email"
                    placeholder={activeAccount === 'preventivi' ? 'preventivi@machenoleggio.it' : 'supporto@machenoleggio.it'}
                    value={currentData.smtp_user}
                    onChange={(e) => handleSmtpChange('smtp_user', e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                
                {/* Password */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-slate-500" />
                    Password *
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={currentData.smtp_password}
                      onChange={(e) => handleSmtpChange('smtp_password', e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Criptazione */}
                <div className="space-y-2">
                  <Label>Criptazione</Label>
                  <Select 
                    value={currentData.smtp_encryption} 
                    onValueChange={(v) => handleSmtpChange('smtp_encryption', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SSL">SSL (Porta 465)</SelectItem>
                      <SelectItem value="TLS">STARTTLS (Porta 587)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Nome Mittente */}
                <div className="space-y-2">
                  <Label>Nome Mittente</Label>
                  <Input
                    placeholder="MaCheNoleggio"
                    value={currentData.smtp_from_name}
                    onChange={(e) => handleSmtpChange('smtp_from_name', e.target.value)}
                  />
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleSaveSmtp}
                  disabled={savingSmtp || !currentData.smtp_host || !currentData.smtp_user}
                >
                  {savingSmtp ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <Settings className="w-4 h-4 mr-2" />
                      Salva senza Test
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleTestSmtp}
                  disabled={testingSmtp || !currentData.smtp_host || !currentData.smtp_user || !currentData.smtp_password}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {testingSmtp ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Invio test...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Test e Salva
                    </>
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 mt-3">
                * Il test invierà un'email di prova all'indirizzo configurato ({currentData.smtp_user || 'non impostato'})
              </p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettingsPage;
